import { useState } from 'react';
import {
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { dialogClose, dialogOpen } from '@/modules/MuiDialog';
import { ACCENT, ACCENT_HOVER } from '../_shared';

export interface BuyerInfo {
    buyerName: string;
    buyerEmail: string;
    buyerTel: string;
}

interface BuyerInfoDialogBodyProps {
    initial: BuyerInfo;
    onConfirm: (value: BuyerInfo) => void;
    onCancel: () => void;
}

/**
 * BasicDialog의 innerElement는 DialogTitle/Content/Actions를 통째로 받는 슬롯이므로
 * 본문 전체를 여기서 직접 구성한다.
 */
const BuyerInfoDialogBody = ({ initial, onConfirm, onCancel }: BuyerInfoDialogBodyProps) => {
    const [value, setValue] = useState<BuyerInfo>(initial);
    const [submitting, setSubmitting] = useState(false);

    const update = (key: keyof BuyerInfo, v: string) => {
        setValue((prev) => ({ ...prev, [key]: v }));
    };

    const handleConfirm = async () => {
        const name = value.buyerName.trim();
        const email = value.buyerEmail.trim();
        const tel = value.buyerTel.trim();

        if (!name) {
            toast.error('이름을 입력해주세요');
            return;
        }
        if (!email) {
            toast.error('이메일을 입력해주세요');
            return;
        }
        if (!tel) {
            toast.error('연락처를 입력해주세요');
            return;
        }

        setSubmitting(true);
        try {
            await onConfirm({ buyerName: name, buyerEmail: email, buyerTel: tel });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <DialogTitle sx={{ fontWeight: 800 }}>결제 정보 확인</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5} sx={{ pt: 0.5, minWidth: { sm: 360 } }}>
                    <Typography variant="caption" color="text.secondary">
                        결제 카드 등록과 영수증 발송에 사용돼요.
                    </Typography>
                    <TextField
                        label="이름"
                        value={value.buyerName}
                        onChange={(e) => update('buyerName', e.target.value)}
                        size="small"
                        fullWidth
                        required
                    />
                    <TextField
                        label="이메일"
                        type="email"
                        value={value.buyerEmail}
                        onChange={(e) => update('buyerEmail', e.target.value)}
                        size="small"
                        fullWidth
                        required
                    />
                    <TextField
                        label="연락처"
                        value={value.buyerTel}
                        onChange={(e) => update('buyerTel', e.target.value.replace(/\D/g, ''))}
                        placeholder="01012345678"
                        helperText="휴대폰 번호 (숫자만)"
                        size="small"
                        fullWidth
                        required
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onCancel}
                    disabled={submitting}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none', cursor: 'pointer' }}
                >
                    취소
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={submitting}
                    variant="contained"
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{
                        bgcolor: ACCENT,
                        color: '#fff',
                        borderRadius: 2,
                        textTransform: 'none',
                        cursor: 'pointer',
                        transition: 'transform 120ms ease, background-color 120ms ease',
                        '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                        '&.Mui-disabled': { bgcolor: ACCENT, opacity: 0.6, color: '#fff' },
                    }}
                >
                    {submitting ? '저장 중…' : '계속'}
                </Button>
            </DialogActions>
        </>
    );
};

/**
 * BuyerInfo 입력 다이얼로그.
 *
 * - 검증 실패(필드 비어 있음) 시 toast만 띄우고 닫지 않음.
 * - 사용자가 confirm/cancel 모두 다이얼로그를 직접 닫고 Promise를 resolve.
 * - backdrop/onExited로 닫히는 경우 null로 resolve.
 */
export function promptBuyerInfo(initial: BuyerInfo): Promise<BuyerInfo | null> {
    return new Promise((resolve) => {
        let resolved = false;
        const safeResolve = (v: BuyerInfo | null) => {
            if (resolved) return;
            resolved = true;
            resolve(v);
        };

        dialogOpen({
            maxWidth: 'xs' as any,
            fullWidth: true,
            innerElement: (
                <BuyerInfoDialogBody
                    initial={initial}
                    onConfirm={(value) => {
                        dialogClose();
                        safeResolve(value);
                    }}
                    onCancel={() => {
                        dialogClose();
                        safeResolve(null);
                    }}
                />
            ),
            onExited: () => safeResolve(null),
        });
    });
}
